/**
 * PostMessage Bridge
 * Handles secure communication between the embedded app and the host application.
 * Validates origins against ALLOWED_HOST_ORIGINS before processing messages.
 */

import type { OutboundMessage, InboundMessage } from "@/types/messaging.types";
import { environment } from "@constants/environment";
import { loggerClient } from "@utils/logger";

/**
 * Detect whether the app is running inside an iframe/webview.
 */
export function isEmbedded(): boolean {
  try {
    return globalThis.self !== globalThis.top;
  } catch {
    // Cross-origin iframe — we are embedded
    return true;
  }
}

/**
 * Get the host origin for postMessage targeting.
 * Derives the actual parent origin from document.referrer and validates it
 * against ALLOWED_HOST_ORIGINS. This ensures postMessage targets the real
 * host frame rather than blindly picking the first configured origin.
 * Returns undefined if the referrer is absent or not in the allow-list.
 * When ALLOWED_HOST_ORIGINS is "*" (local dev), the referrer origin is used directly.
 */
function getTargetOrigin(): string | undefined {
  try {
    const referrerOrigin = new URL(document.referrer).origin;
    const allowed = environment.ALLOWED_HOST_ORIGINS.split(",")
      .map((o) => o.trim())
      .filter(Boolean);
    if (allowed.includes("*")) return referrerOrigin;
    return allowed.includes(referrerOrigin) ? referrerOrigin : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Validate that an incoming message origin is in the allow-list.
 * When ALLOWED_HOST_ORIGINS is "*" (local dev), all origins are accepted.
 */
function isOriginAllowed(origin: string): boolean {
  const allowed = environment.ALLOWED_HOST_ORIGINS.split(",")
    .map((o) => o.trim())
    .filter(Boolean);
  return allowed.includes("*") || allowed.includes(origin);
}

/**
 * Send a message to the host/parent application.
 * No-op when not running in an embedded context.
 * No-op (with error log) when ALLOWED_HOST_ORIGINS is not configured.
 */
export function sendMessageToHost(message: OutboundMessage): void {
  if (!isEmbedded()) {
    if (!environment.production) {
      loggerClient.debug("[EmbedBridge] Not embedded, logging message", {
        message
      });
    }
    return;
  }

  const targetOrigin = getTargetOrigin();
  if (!targetOrigin) {
    loggerClient.error(
      "[EmbedBridge] No ALLOWED_HOST_ORIGINS configured — cannot send message."
    );
    return;
  }
  globalThis.parent.postMessage(message, targetOrigin);
}

/**
 * Subscribe to messages from the host application.
 * Returns an unsubscribe function for cleanup.
 */
export function onHostMessage(
  handler: (message: InboundMessage) => void
): () => void {
  const listener = (event: MessageEvent) => {
    if (!isOriginAllowed(event.origin)) {
      return;
    }

    const data = event.data as InboundMessage | undefined;
    if (!data || typeof data.type !== "string") {
      return;
    }

    handler(data);
  };

  globalThis.addEventListener("message", listener);
  return () => globalThis.removeEventListener("message", listener);
}
